import log from './log.js';

/**
 * Web Serial API ile seri port iletişimi için yardımcı sınıf
 */
class SerialHelper {
    constructor() {
        this.port = null;
        this.reader = null;
        this.writer = null;
        this.readLoop = null;
        this.connected = false;
        this.baudRate = 9600;
        this.encoder = new TextEncoder();
        this.decoder = new TextDecoder();
        this.readLoopActive = false;

        // Callback fonksiyonları
        this.onData = () => {};
        this.onConnectionChanged = () => {};

        // Web Serial API desteğini kontrol et (hata fırlatmadan)
        this.supported = false;
        try {
            this.checkSupport();
            this.supported = true;
        } catch (error) {
            console.warn('Web Serial API desteklenmiyor:', error.message);
            this.supported = false;
        }
    }

    /**
     * Debug log mesajları
     * @param {string} message - Kaydedilecek mesaj
     */
    log (message) {
        log.info(`SerialHelper: ${message}`);
    }

    checkSupport() {
        try {
            if (typeof navigator === 'undefined' || !navigator) {
                throw new Error('navigator nesnesi tanımlı değil');
            }

            // Web Serial API kontrolü
            if (!('serial' in navigator)) {
                // Edge için özel mesaj
                const isEdge = /Edg/.test(navigator.userAgent);
                if (isEdge) {
                    throw new Error('Edge tarayıcısında Web Serial API etkinleştirilmemiş. edge://flags adresinden "Experimental Web Platform features" seçeneğini etkinleştirin.');
                } else {
                    throw new Error('Web Serial API bu tarayıcıda desteklenmiyor. Chrome veya Edge kullanın.');
                }
            }

            // Fonksiyon kontrolü
            if (typeof navigator.serial.requestPort !== 'function') {
                throw new Error('Web Serial API fonksiyonları mevcut değil');
            }

            return true;
        } catch (error) {
            console.warn('Web Serial API kontrol hatası:', error.message);
            throw error;
        }
    }

    isSerialSupported() {
        return this.supported;
    }

    /**
     * Seri port bağlantısı durumunu kontrol eder
     * @returns {boolean} Bağlıysa true
     */
    isConnected() {
        return this.connected || false;
    }

    /**
     * Port seçim diyaloğunu gösterir
     * @returns {Promise<boolean>} Port seçildiyse true
     */
    async requestPort() {
        try {
            if (!this.isSerialSupported()) {
                throw new Error('Web Serial API bu tarayıcıda desteklenmiyor');
            }

            // Port seçimini başlat
            const port = await navigator.serial.requestPort();
            this.port = port;
            return true;
        } catch (error) {
            console.error('Port seçim hatası:', error);
            if (error.name === 'NotFoundError') {
                throw new Error('Hiçbir seri port bulunamadı');
            } else if (error.name === 'SecurityError') {
                throw new Error('Güvenlik hatası: Lütfen HTTPS veya localhost kullanın');
            } else {
                throw error;
            }
        }
    }

    /**
     * Seri porta bağlanır
     * @param {number} baudRate - Baud rate değeri
     * @returns {Promise<boolean>} Bağlantı başarılıysa true
     */
    async connect(baudRate = 9600) {
        try {
            if (!this.port) {
                throw new Error('Önce bir port seçilmelidir');
            }

            await this.port.open({ baudRate });
            this.connected = true;

            // Basit okuma ve yazma setup'ı (mock uyumlu)
            try {
                // Gerçek API için stream setup
                if (this.port.readable && this.port.readable.pipeTo) {
                    const textDecoder = new TextDecoderStream();
                    this.port.readable.pipeTo(textDecoder.writable);
                    this.reader = textDecoder.readable.getReader();
                } else {
                    // Mock için basit reader
                    this.reader = this.port.readable ? this.port.readable.getReader() : null;
                }

                if (this.port.writable && this.port.writable.getWriter) {
                    this.writer = this.port.writable.getWriter();
                } else {
                    // Mock için basit writer
                    this.writer = this.port.writable ? this.port.writable.getWriter() : null;
                }
            } catch (streamError) {
                console.warn('Stream setup hatası (mock kullanılıyor):', streamError.message);
                // Mock durumunda stream hatalarını yok say
            }

            console.log('Serial port bağlantısı başarılı');
            return true;
        } catch (error) {
            console.error('Bağlantı hatası:', error);
            this.connected = false;
            throw error;
        }
    }

    /**
     * Seri port bağlantısını kapatır
     */
    async disconnect() {
        try {
            if (this.reader) {
                await this.reader.cancel();
                this.reader = null;
            }
            if (this.writer) {
                await this.writer.close();
                this.writer = null;
            }
            if (this.port) {
                await this.port.close();
                this.port = null;
            }
            this.connected = false;
            return true;
        } catch (error) {
            console.error('Bağlantı kesme hatası:', error);
            throw error;
        }
    }

    /**
     * Veri okuma döngüsünü başlatır
     * @private
     */
    async startReading() {
        try {
            while (this.connected) {
                const { value, done } = await this.reader.read();
                if (done) {
                    this.reader.releaseLock();
                    break;
                }
                if (value) {
                    // Gelen veriyi işle
                    console.log('Gelen veri:', value);
                    this.onData(value);
                }
            }
        } catch (error) {
            console.error('Okuma hatası:', error);
            this.connected = false;
            this.disconnect();
        }
    }

    /**
     * Seri porta veri gönderir
     * @param {string} data - Gönderilecek veri
     * @returns {Promise<boolean>} Başarılıysa true
     */
    async sendData(data) {
        if (!this.connected || !this.writer) {
            console.error('Veri gönderilemiyor: Bağlantı yok');
            return false;
        }
        
        try {
            // Veriyi diziye dönüştür
            const bytes = this.encoder.encode(data);
            
            // Veriyi gönder
            await this.writer.write(bytes);
            
            console.log(`Veri gönderildi: ${data}`);
            return true;
        } catch (error) {
            console.error(`Veri gönderme hatası: ${error.message}`);
            
            // Hata durumunda bağlantıyı kapat
            await this.disconnect();
            return false;
        }
    }
}

export default SerialHelper; 